import apiClient from './apiClient';

export interface Dispute {
  id: string;
  job: {
    id: string;
    title: string;
    budget: number;
    client: string;
    artisan?: string;
  };
  initiated_by: {
    id: string;
    name: string;
    type: string;
  };
  reason: string;
  description: string;
  status: 'open' | 'under_review' | 'resolved' | 'escalated' | 'closed';
  created_at: string;
  resolved_at?: string;
  evidence_count: number;
}

export interface DisputeDetail extends Omit<Dispute, 'job'> {
  job: {
    id: string;
    title: string;
    description: string;
    budget: number;
    escrow_amount: number | null;
    client: {
      id: string;
      name: string;
      phone: string;
    };
    artisan?: {
      id: string;
      name: string;
      rating: number;
    };
  };
  evidence_urls: string[];
  admin_notes: string;
  resolution_type?: string | null;
  artisan_payout?: number | null;
  customer_refund?: number | null;
}

export interface DisputeListResponse {
  count: number;
  disputes: Dispute[];
}

export interface ResolveDisputeParams {
  resolution_type: 'full_refund' | 'partial_split' | 'reassign' | 'closed';
  artisan_payout?: number;
  customer_refund?: number;
  admin_notes?: string;
}

class DisputeService {
  async getDisputes(status?: string): Promise<DisputeListResponse> {
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;

      const response = await apiClient.get(`/admin/disputes/`, { params });
      const raw = response.data?.data || response.data || {};

      const rawList: any[] = raw.disputes || raw.results || [];
      const count: number = raw.count ?? rawList.length;

      // Admin backend returns `reported_by`; normalize to `initiated_by`
      const disputes: Dispute[] = rawList.map((d: any) => ({
        ...d,
        initiated_by: d.initiated_by ?? (d.reported_by
          ? { id: d.reported_by.id, name: d.reported_by.name, type: d.reported_by.type }
          : null),
      }));

      return { disputes, count };
    } catch (error) {
      console.error('Error fetching disputes:', error);
      throw error;
    }
  }

  async getDisputeDetail(disputeId: string): Promise<DisputeDetail> {
    try {
      const response = await apiClient.get(`/admin/disputes/${disputeId}/`);
      const raw = response.data?.data || response.data;

      // Admin backend: job.client is an object, but uses reported_by / budget_max
      if (raw?.job?.client && typeof raw.job.client === 'object') {
        const pc = raw.photo_comparison || {};
        const evidenceUrls: string[] = [];
        if (pc.dispute_evidence) evidenceUrls.push(pc.dispute_evidence);
        if (Array.isArray(pc.before_photos)) evidenceUrls.push(...pc.before_photos);
        if (pc.after_photo) evidenceUrls.push(pc.after_photo);

        return {
          id: raw.id || disputeId,
          status: raw.status || 'open',
          reason: raw.reason || '',
          description: raw.description || '',
          evidence_urls: evidenceUrls,
          evidence_count: evidenceUrls.length,
          created_at: raw.created_at || '',
          resolved_at: raw.resolved_at,
          admin_notes: raw.admin_notes || '',
          resolution_type: raw.resolution_type ?? null,
          artisan_payout: raw.artisan_payout ?? null,
          customer_refund: raw.customer_refund ?? null,
          job: {
            id: raw.job.id || '',
            title: raw.job.title || '',
            description: raw.job.description || '',
            budget: raw.job.budget ?? raw.job.budget_max ?? raw.job.budget_min ?? 0,
            escrow_amount: raw.job.escrow_amount ?? null,
            client: {
              id: raw.job.client.id || '',
              name: raw.job.client.name || 'Unknown',
              phone: raw.job.client.phone || '',
            },
            artisan: raw.job.artisan
              ? {
                  id: raw.job.artisan.id || '',
                  name: raw.job.artisan.name || '',
                  rating: raw.job.artisan.rating || 0,
                }
              : undefined,
          },
          initiated_by: raw.initiated_by ?? (raw.reported_by
            ? { id: raw.reported_by.id, name: raw.reported_by.name, type: raw.reported_by.type }
            : { id: '', name: 'Unknown', type: 'client' }),
        };
      }

      // Legacy flat shape from get_dispute_context()
      return {
        id: raw.dispute_id || raw.id || disputeId,
        status: raw.status || 'open',
        reason: raw.dispute_reason || raw.reason || '',
        description: raw.description || '',
        evidence_urls: raw.evidence_url ? [raw.evidence_url] : [],
        evidence_count: raw.evidence_url ? 1 : 0,
        created_at: raw.created_at || '',
        resolved_at: raw.resolved_at,
        admin_notes: raw.admin_notes || '',
        resolution_type: raw.resolution_type || null,
        artisan_payout: raw.artisan_payout ?? null,
        customer_refund: raw.customer_refund ?? null,
        job: {
          id: raw.job_id || '',
          title: raw.job_title || '',
          description: raw.job_description || '',
          budget: raw.budget || 0,
          escrow_amount: raw.escrow_amount || null,
          client: {
            id: raw.customer?.id || '',
            name: raw.customer?.name || 'Unknown',
            phone: raw.customer?.phone || '',
          },
          artisan: raw.artisan?.name
            ? {
                id: raw.artisan?.id || '',
                name: raw.artisan.name,
                rating: raw.artisan.rating || 0,
              }
            : undefined,
        },
        initiated_by: {
          id: raw.initiated_by?.id || '',
          name: raw.reported_by || raw.initiated_by?.name || 'Unknown',
          type: raw.initiated_by?.type || 'client',
        },
      };
    } catch (error) {
      console.error('Error fetching dispute detail:', error);
      throw error;
    }
  }

  /** Simple status transition — under_review or escalated (no escrow movement) */
  async updateDisputeStatus(
    disputeId: string,
    newStatus: string,
    notes?: string
  ): Promise<any> {
    try {
      const response = await apiClient.patch(`/admin/disputes/${disputeId}/`, {
        status: newStatus,
        notes,
      });
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error updating dispute status:', error);
      throw error;
    }
  }

  /** Full resolution with escrow control — POST /resolve/ */
  async resolveDispute(
    disputeId: string,
    params: ResolveDisputeParams
  ): Promise<any> {
    try {
      const response = await apiClient.post(
        `/admin/disputes/${disputeId}/resolve/`,
        params
      );
      return response.data?.data || response.data;
    } catch (error) {
      console.error('Error resolving dispute:', error);
      throw error;
    }
  }

  /** Convenience wrappers (kept for backward compatibility) */
  async escalateDispute(disputeId: string, notes?: string): Promise<any> {
    return this.updateDisputeStatus(disputeId, 'escalated', notes);
  }

  async closeDispute(disputeId: string, notes?: string): Promise<any> {
    return this.resolveDispute(disputeId, { resolution_type: 'closed', admin_notes: notes });
  }
}

export default new DisputeService();
